import { createFileRoute } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AccountSettings } from "@/features/account";
import { useMyOrg } from "@/features/orgs";
import { TrackerSettingsCard } from "@/features/tracker";

export const Route = createFileRoute("/_authenticated/org/settings")({
  component: SettingsPage,
});

const SUPPORT_EMAIL = "egc@themotherhood.com";

/**
 * The manager's settings: their own account, plus what The Motherhood holds
 * about the organisation. The organisation's details are set up by The
 * Motherhood and changed by asking, so they are shown, not edited.
 */
function SettingsPage() {
  const org = useMyOrg();
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow="Your team"
        title="Settings"
        description="Your account, and the organisation as The Motherhood has it."
      />
      <AccountSettings>
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="font-semibold text-foreground text-lg">Your organisation</h2>
            <p className="text-muted-foreground text-sm">
              Set up by The Motherhood. To change any of it, or your brand setup, email them.
            </p>
          </CardHeader>
          <CardContent>
            {org.isPending ? (
              <Skeleton className="h-12 rounded-md" />
            ) : org.data ? (
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-[0.08em]">
                    Name
                  </dt>
                  <dd className="text-foreground">{org.data.name}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-[0.08em]">
                    Kind of organisation
                  </dt>
                  <dd className="text-foreground">{org.data.industry || "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-[0.08em]">
                    Support
                  </dt>
                  <dd>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Mail className="size-3.5" /> {SUPPORT_EMAIL}
                    </a>
                  </dd>
                </div>
              </dl>
            ) : null}
          </CardContent>
        </Card>
        <TrackerSettingsCard />
      </AccountSettings>
    </div>
  );
}
